import { useCallback, useRef } from 'react';
import { Card, CardContent } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { motion } from 'framer-motion';

// local imports
import PharmacyDetailCard from './pharmacy-detail-card';
import { PharmacyService } from 'app/services';
import { useStateCallback } from 'app/hooks';
import { usePharmacyDetailService } from 'app/hooks/services';
import { ValidationSchema } from 'app/utils';
import { PharmacyCrudForm } from 'app/components/organisms/forms';
import { Loader, NoDataTableComponent } from 'app/components/atoms';

const PharmacyEdit = ({ pharmacyId }) => {
  const formikRef = useRef();
  const [loading, setLoading] = useStateCallback(false);

  // Fetch pharmacy detail data first;
  const { service, handleRefresh } = usePharmacyDetailService(pharmacyId);

  // Submit Update Form
  const handleSubmit = useCallback(
    async values => {
      const {
        second_telephone,
        email,
        city_id,
        district_id,
        region_id,
        address,
        location,
        ...rest
      } = values;
      const valueToUse = {
        ...rest,
        ...(second_telephone ? { second_telephone } : {}),
        ...(email ? { email } : {}),
        ...(address ? { address } : {}),
        city_id: city_id.value,
        district_id: district_id.value,
        region_id: region_id.value
      };

      setLoading(true, async () => {
        const serviceFn = () => PharmacyService.updatePharmacy(pharmacyId, valueToUse);
        try {
          await serviceFn();
        } catch (err) {
          console.log('Error -> ', err);
        } finally {
          setLoading(false);
        }
      });
    },
    [setLoading, pharmacyId]
  );

  // variables - states
  const isDataLoading = !service.data && (!service.isInitialized || service.isLoading);
  const isDataExist = !!service.data && !service.isNetworkError && !service.isError;
  const {
    id,
    status,
    is_turp_pharmacy,
    is_on_duty,
    created_at,
    code,
    start_time,
    end_time,
    coordinates,
    ...rest
  } = !!service.data && service?.data;
  const pharmacyCoordinates = !!service.data && JSON.stringify(coordinates);
  const initialVal = { ...rest, location: pharmacyCoordinates };

  return (
    <>
      <Loader loading={isDataLoading} />
      {isDataExist ? (
        <motion.div initial={{ scale: 0, opacity: 0.2 }} animate={{ scale: 1, opacity: 1 }}>
          {/* PharmacyInfo Card */}
          <PharmacyDetailCard
            duty={is_on_duty}
            isTurp={is_turp_pharmacy}
            id={id}
            code={code}
            times={{ start_time, end_time }}
            handleRefresh={handleRefresh}
          />

          {/* CRUD Form */}
          <Card elevation={4}>
            <CardContent>
              <Formik
                innerRef={formikRef}
                enableReinitialize
                initialValues={initialVal}
                validationSchema={ValidationSchema.pharmacyCrudSchema}
                onSubmit={handleSubmit}>
                <Form>
                  <PharmacyCrudForm isEdit={true} loading={loading} />
                </Form>
              </Formik>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        !isDataLoading && <NoDataTableComponent />
      )}
    </>
  );
};

export default PharmacyEdit;
